/**
 * Página reutilizável para as 3 perguntas do onboarding
 * Renderiza input text ou combobox com sugestões
 */

import {
  Box,
  Container,
  TextField,
  Button,
  Stack,
  Typography,
  LinearProgress,
  Autocomplete,
  Paper,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { authService } from '../../services/authService';

export default function QuestionPage({ questionNumber, template, onboarding }) {
  const [inputValue, setInputValue] = useState('');

  // Resetar input quando muda a pergunta
  useEffect(() => {
    const responseKey =
      questionNumber === 1
        ? 'professionalName'
        : questionNumber === 2
        ? 'placeName'
        : 'serviceName';
    
    // Se houver um valor guardado para esta pergunta, carregar; senão limpar
    const savedValue = onboarding.responses[responseKey] || '';
    setInputValue(savedValue);
  }, [questionNumber, onboarding.responses]);

  const responseKey =
    questionNumber === 1
      ? 'professionalName'
      : questionNumber === 2
      ? 'placeName'
      : 'serviceName';

  // Obter dados da pergunta do template
  const questionData = useMemo(() => {
    if (!template?.onboarding) return null;

    switch (questionNumber) {
      case 1:
        return template.onboarding.professional;
      case 2:
        return template.onboarding.place;
      case 3:
        return template.onboarding.service;
      default:
        return null;
    }
  }, [template, questionNumber]);

  const progressPercent = (questionNumber / 3) * 100;

  const handleNext = () => {
    if (inputValue.trim() === '') {
      return;
    }

    onboarding.setResponse(responseKey, inputValue);

    if (questionNumber < 3) {
      onboarding.goToQuestion(questionNumber + 1);
    } else {
      // Última pergunta - ir para processamento
      onboarding.setResponse(responseKey, inputValue);
      onboarding.startProcessing();
    }
  };

  const handleBack = () => {
    onboarding.goBack();
  };

  const handleSkip = () => {
    // Pular vai direto para admin, sem card de resumo
    onboarding.completeOnboarding();
    if (onboarding.theme) {
      const userId = authService.getUserInfo()?.userId;
      const key = userId ? `agendario_theme_${userId}` : 'agendario_theme';
      localStorage.setItem(key, onboarding.theme);
    }
    window.location.href = '/dashboard';
  };

  if (!questionData) {
    return (
      <Box sx={{ textAlign: 'center', p: 2 }}>
        <Typography>Carregando pergunta...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#ffffff',
        padding: { xs: '32px 16px', md: '48px 32px' },
        boxSizing: 'border-box',
      }}
    >
      {/* Top Info Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 6, maxWidth: 700, width: '100%' }}>
        <Typography sx={{ color: '#999999', fontSize: '0.85rem', fontWeight: 500 }}>
          Configuração inicial
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={handleSkip}
          sx={{
            color: '#999999',
            textTransform: 'none',
            fontSize: '0.85rem',
            '&:hover': {
              color: '#666666',
              backgroundColor: 'transparent',
            },
          }}
        >
          Pular
        </Button>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ mb: 6, maxWidth: 700, width: '100%' }}>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 4,
            borderRadius: '2px',
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#1a1a1a',
            },
          }}
        />
        <Typography
          sx={{
            color: '#999999',
            fontSize: '0.8rem',
            mt: 1,
            textAlign: 'right',
            fontWeight: 400,
          }}
        >
          Pergunta {questionNumber} de 3
        </Typography>
      </Box>

      {/* Info Box */}
      <Box sx={{ mb: 6, maxWidth: 700, width: '100%', textAlign: 'center' }}>
        {(() => {
          const messages = {
            1: { greeting: "Vamos nos conhecer melhor? 👋", description: "Responda <strong>3 perguntas rápidas</strong> e desbloqueie um ambiente completo! Profissional, serviço, local e agenda já configurados para você começar a agendar! 🚀" },
            2: { greeting: "Estamos quase lá! 🎯", description: "Mais uma pergunta e você terá tudo pronto para começar!" },
            3: { greeting: "Última pergunta! 🏁", description: "Depois disso, seu ambiente estará 100% configurado e pronto para uso!" },
          };
          const msg = messages[questionNumber] || messages[1];
          return (
            <>
              <Typography
                sx={{
                  color: '#666666',
                  fontSize: '1rem',
                  fontWeight: 500,
                  lineHeight: 1.4,
                  mb: 1,
                }}
              >
                {msg.greeting}
              </Typography>
              <Typography
                sx={{
                  color: '#1a1a1a',
                  fontSize: '0.95rem',
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                {msg.description.split('<strong>').map((part, idx) => {
                  if (idx === 0) return part;
                  const [bold, rest] = part.split('</strong>');
                  return (
                    <span key={idx}>
                      <strong>{bold}</strong>
                      {rest}
                    </span>
                  );
                })}
              </Typography>
            </>
          );
        })()}
      </Box>

      {/* Main Content - Centered */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 500,
            backgroundColor: '#ffffff',
          }}
        >
          {/* Question Title */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 2.5,
              color: '#1a1a1a',
              fontSize: '1.1rem',
            }}
          >
            {questionData.label}
          </Typography>

          {/* Question Hint */}
          {questionData.hint && (
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                mb: 2,
                fontStyle: 'italic',
              }}
            >
              {questionData.hint}
            </Typography>
          )}

          {/* Input/Autocomplete */}
          <Box sx={{ mb: 3 }}>
            {questionNumber === 3 && questionData.suggestions ? (
              // Autocomplete com sugestões
              <Autocomplete
                freeSolo
                options={questionData.suggestions || []}
                value={inputValue}
                onChange={(event, newValue) => {
                  setInputValue(newValue || '');
                }}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={questionData.placeholder}
                    fullWidth
                    variant="outlined"
                    size="large"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1.1rem',
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  style: { maxHeight: '200px' },
                }}
              />
            ) : (
              // Input text simples
              <TextField
                fullWidth
                placeholder={questionData.placeholder}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                variant="outlined"
                size="large"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleNext();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                  },
                }}
              />
            )}
          </Box>

          {/* Buttons */}
          <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              variant="outlined"
              fullWidth
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Voltar
            </Button>

            <Button
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              variant="contained"
              fullWidth
              disabled={inputValue.trim() === ''}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: '#1a1a1a',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#0f0f0f',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#999999',
                },
              }}
            >
              {questionNumber === 3 ? 'Criar' : 'Próximo'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
